import { baseApi, resourceListTag, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { normalizeRid } from '../../../shared/model/scopeKeys'
import { onecEnvelopeDecoder, type OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { writeCommandBody, type WriteCommandIdentity } from '../../../shared/api/writeCommand'
import type {
  BarcodeMutationResult,
  CatalogRefreshResult,
  CreateProductResult,
  ProductBarcode,
  ProductCandidate,
  ProductCard,
} from '../model/types'
import {
  ONEC_PRODUCT_SCHEMAS,
  decodeBarcodeMutation,
  decodeBarcodes,
  decodeCandidates,
  decodeCard,
  decodeCreateResult,
  decodeDeleteResult,
  decodeSyncCapabilities,
  decodeTriggerSync,
  decodeUpdateResult,
} from './contracts'
import { ONEC_PRODUCT_URLS } from './urls'

/**
 * Endpoints товаров 1С в ОБЩЕМ `baseApi` (R04): имена `onec*`, rid — аргумент
 * КАЖДОГО запроса (часть ключа кэша) и часть каждого тега, плюс тег области
 * ресурса. Ответ проверяется декодером, включая rid конверта.
 */
export const ONEC_PRODUCT_TAG = 'OneCProduct' as const

export interface OneCProductSearchArgs {
  resourceId: string
  q: string
  limit?: number
}

export interface OneCProductBarcodeArgs {
  resourceId: string
  barcode: string
}

export interface OneCProductArgs {
  resourceId: string
  refKey: string
}

export interface OneCResourceArgs {
  resourceId: string
}

interface WriteArgs<T> {
  resourceId: string
  identity: WriteCommandIdentity
  data: T
}

export type OneCCreateProductArgs = WriteArgs<{ name: string; article: string | null; barcode: string | null }>

export interface OneCUpdateProductArgs extends WriteArgs<{ name?: string; article?: string }> {
  refKey: string
}

export interface OneCProductCommandArgs extends WriteArgs<null> {
  refKey: string
}

export interface OneCBarcodeCommandArgs extends WriteArgs<null> {
  refKey: string
  barcode: string
}

const productTags = (resourceId: string, refKey: string) => [
  resourceTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId), refKey),
  resourceListTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId)),
]

export const productsApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_PRODUCT_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    onecProductSearch: build.query<OneCEnvelope<ProductCandidate[]>, OneCProductSearchArgs>({
      query: ({ resourceId, q, limit }) => ({
        url: ONEC_PRODUCT_URLS.search(resourceId),
        params: { q, ...(limit !== undefined ? { limit } : {}) },
        decode: onecEnvelopeDecoder(resourceId, decodeCandidates),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId)),
        resourceScopeTag(normalizeRid(resourceId)),
      ],
    }),
    onecProductByBarcode: build.query<OneCEnvelope<ProductCandidate[]>, OneCProductBarcodeArgs>({
      query: ({ resourceId, barcode }) => ({
        url: ONEC_PRODUCT_URLS.byBarcode(resourceId),
        params: { barcode },
        decode: onecEnvelopeDecoder(resourceId, decodeCandidates),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId)),
        resourceScopeTag(normalizeRid(resourceId)),
      ],
    }),
    onecProductCard: build.query<OneCEnvelope<ProductCard>, OneCProductArgs>({
      query: ({ resourceId, refKey }) => ({
        url: ONEC_PRODUCT_URLS.card(resourceId, refKey),
        decode: onecEnvelopeDecoder(resourceId, decodeCard),
      }),
      providesTags: (_result, _error, { resourceId, refKey }) => [
        resourceTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId), refKey),
        resourceScopeTag(normalizeRid(resourceId)),
      ],
    }),
    onecProductBarcodes: build.query<OneCEnvelope<ProductBarcode[]>, OneCProductArgs>({
      query: ({ resourceId, refKey }) => ({
        url: ONEC_PRODUCT_URLS.barcodes(resourceId, refKey),
        decode: onecEnvelopeDecoder(resourceId, decodeBarcodes),
      }),
      providesTags: (_result, _error, { resourceId, refKey }) => [
        resourceTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId), refKey),
        resourceScopeTag(normalizeRid(resourceId)),
      ],
    }),

    // --- команды: ключ и operationId приходят от операции, не создаются здесь ---
    onecCreateProduct: build.mutation<OneCEnvelope<CreateProductResult>, OneCCreateProductArgs>({
      query: ({ resourceId, identity, data }) => ({
        url: ONEC_PRODUCT_URLS.create(resourceId),
        method: 'POST',
        body: writeCommandBody(ONEC_PRODUCT_SCHEMAS.create, identity, data),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeCreateResult),
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId))],
    }),
    onecUpdateProduct: build.mutation<OneCEnvelope<{ changed: boolean }>, OneCUpdateProductArgs>({
      query: ({ resourceId, refKey, identity, data }) => ({
        url: ONEC_PRODUCT_URLS.product(resourceId, refKey),
        method: 'PATCH',
        body: writeCommandBody(ONEC_PRODUCT_SCHEMAS.update, identity, data),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeUpdateResult),
      }),
      invalidatesTags: (_result, _error, { resourceId, refKey }) => productTags(resourceId, refKey),
    }),
    onecMarkProductDeleted: build.mutation<OneCEnvelope<{ deleted: boolean }>, OneCProductCommandArgs>({
      query: ({ resourceId, refKey, identity }) => ({
        url: ONEC_PRODUCT_URLS.product(resourceId, refKey),
        method: 'DELETE',
        body: writeCommandBody(ONEC_PRODUCT_SCHEMAS.delete, identity, null),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeDeleteResult),
      }),
      invalidatesTags: (_result, _error, { resourceId, refKey }) => productTags(resourceId, refKey),
    }),
    onecAddProductBarcode: build.mutation<OneCEnvelope<BarcodeMutationResult>, OneCBarcodeCommandArgs>({
      query: ({ resourceId, refKey, barcode, identity }) => ({
        url: ONEC_PRODUCT_URLS.barcodes(resourceId, refKey),
        method: 'POST',
        body: writeCommandBody(ONEC_PRODUCT_SCHEMAS.addBarcode, identity, { barcode }),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeBarcodeMutation),
      }),
      invalidatesTags: (_result, _error, { resourceId, refKey }) => productTags(resourceId, refKey),
    }),
    onecDeleteProductBarcode: build.mutation<OneCEnvelope<BarcodeMutationResult>, OneCBarcodeCommandArgs>({
      query: ({ resourceId, refKey, barcode, identity }) => ({
        url: ONEC_PRODUCT_URLS.barcode(resourceId, refKey, barcode),
        method: 'DELETE',
        body: writeCommandBody(ONEC_PRODUCT_SCHEMAS.deleteBarcode, identity, null),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeBarcodeMutation),
      }),
      invalidatesTags: (_result, _error, { resourceId, refKey }) => productTags(resourceId, refKey),
    }),
    onecSetMainProductBarcode: build.mutation<OneCEnvelope<BarcodeMutationResult>, OneCBarcodeCommandArgs>({
      query: ({ resourceId, refKey, barcode, identity }) => ({
        url: ONEC_PRODUCT_URLS.mainBarcode(resourceId, refKey),
        method: 'PUT',
        body: writeCommandBody(ONEC_PRODUCT_SCHEMAS.setMainBarcode, identity, { barcode }),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeBarcodeMutation),
      }),
      invalidatesTags: (_result, _error, { resourceId, refKey }) => productTags(resourceId, refKey),
    }),
    /** Ревизия базы перед обновлением каталога: читается свежей, не из кэша поиска. */
    onecSyncCapabilities: build.query<{ configurationVersion: string; refreshKinds: string[] }, OneCResourceArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_PRODUCT_URLS.syncCapabilities(resourceId),
        decode: decodeSyncCapabilities(resourceId),
      }),
    }),
    onecTriggerCatalogSync: build.mutation<OneCEnvelope<CatalogRefreshResult>, WriteArgs<null> & { refreshKind: string }>({
      query: ({ resourceId, refreshKind, identity }) => ({
        url: ONEC_PRODUCT_URLS.triggerSync(resourceId, refreshKind),
        method: 'POST',
        body: writeCommandBody(ONEC_PRODUCT_SCHEMAS.triggerSync, identity, null),
        idempotencyKey: identity.idempotencyKey,
        decode: onecEnvelopeDecoder(resourceId, decodeTriggerSync),
      }),
      // Зеркало каталога обновлено — найденное этой базой перечитывается.
      invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(ONEC_PRODUCT_TAG, normalizeRid(resourceId))],
    }),
  }),
})

export const {
  onecProductSearch,
  onecProductByBarcode,
  onecProductCard,
  onecProductBarcodes,
  onecCreateProduct,
  onecUpdateProduct,
  onecMarkProductDeleted,
  onecAddProductBarcode,
  onecDeleteProductBarcode,
  onecSetMainProductBarcode,
  onecSyncCapabilities,
  onecTriggerCatalogSync,
} = productsApi.endpoints
